import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PortalAccess = () => {
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setLoginForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    
    // Mock login validation
    setTimeout(() => {
      if (loginForm?.email === 'contractor@openbooq.com' && loginForm?.password === 'contractor123') {
        alert('Login successful! Redirecting to contractor portal...');
      } else {
        alert('Invalid credentials. Please use:\nEmail: contractor@openbooq.com\nPassword: contractor123');
      }
      setIsLoading(false);
    }, 1500);
  };

  const portalFeatures = [
    {
      id: 1,
      icon: "Calendar",
      title: "Job Management",
      description: "View assigned jobs, track progress, and manage your schedule with our intuitive calendar interface."
    },
    {
      id: 2,
      icon: "DollarSign",
      title: "Earnings Dashboard",
      description: "Monitor your earnings, view payment history, and track performance metrics in real-time."
    },
    {
      id: 3,
      icon: "MessageSquare",
      title: "Customer Communication",
      description: "Direct messaging with customers, photo sharing, and progress updates all in one place."
    },
    {
      id: 4,
      icon: "Camera",
      title: "Photo Documentation",
      description: "Upload before/after photos, document progress, and maintain comprehensive project records."
    },
    {
      id: 5,
      icon: "FileText",
      title: "Invoice & Payments",
      description: "Generate invoices, track payments, and manage all financial aspects of your projects."
    },
    {
      id: 6,
      icon: "BarChart3",
      title: "Performance Analytics",
      description: "Detailed analytics on job completion rates, customer satisfaction, and business growth."
    }
  ];

  const recentUpdates = [
    {
      id: 1,
      title: "New Mobile App Features",
      description: "Enhanced photo upload and GPS tracking capabilities now available.",
      date: "November 1, 2024",
      type: "Feature"
    },
    {
      id: 2,
      title: "Payment Processing Improvements",
      description: "Faster payment processing with same-day deposit options for completed jobs.",
      date: "October 28, 2024",
      type: "Update"
    },
    {
      id: 3,
      title: "Winter Weather Guidelines",
      description: "New safety protocols and scheduling guidelines for winter roofing projects.",
      date: "October 25, 2024",
      type: "Policy"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center space-y-6 mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full">
            <Icon name="Lock" size={16} className="text-primary mr-2" />
            <span className="text-sm font-medium text-primary">Contractor Portal</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-jakarta font-bold text-text-primary">
            Access Your Contractor Portal
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Manage your jobs, track earnings, communicate with customers, and access all the tools 
            you need to run your roofing business efficiently.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Login Form */}
          <div className="bg-surface rounded-2xl p-8 lg:p-12">
            <div className="space-y-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center mx-auto">
                  <Icon name="User" size={24} className="text-white" />
                </div>
                <h3 className="text-2xl font-jakarta font-bold text-text-primary">
                  Contractor Login
                </h3>
                <p className="text-text-secondary">
                  Access your personalized contractor dashboard
                </p>
              </div>

              {!showForgotPassword ? (
                <form onSubmit={handleLogin} className="space-y-6">
                  <Input
                    label="Email Address"
                    type="email"
                    name="email"
                    value={loginForm?.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    required
                  />
                  
                  <Input
                    label="Password"
                    type="password"
                    name="password"
                    value={loginForm?.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    required
                  />
                  
                  <div className="flex items-center justify-between">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="w-4 h-4 text-primary border-border rounded focus:ring-primary" />
                      <span className="text-sm text-text-secondary">Remember me</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-primary hover:text-primary/80 transition-colors duration-200"
                    >
                      Forgot password?
                    </button>
                  </div>
                  
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    loading={isLoading}
                  >
                    Sign In to Portal
                  </Button>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="text-center">
                    <h4 className="font-jakarta font-semibold text-text-primary mb-2">Reset Password</h4>
                    <p className="text-sm text-text-secondary">
                      Enter your email address and we'll send you a reset link
                    </p>
                  </div>
                  
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="Enter your email"
                    required
                  />
                  
                  <div className="space-y-3">
                    <Button variant="default" size="lg" fullWidth>
                      Send Reset Link
                    </Button>
                    <Button
                      variant="ghost"
                      size="lg"
                      fullWidth
                      onClick={() => setShowForgotPassword(false)}
                    >
                      Back to Login
                    </Button>
                  </div>
                </div>
              )}

              {/* Demo Credentials */}
              <div className="bg-primary/10 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={16} className="text-primary mt-0.5" />
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-primary">Demo Credentials</h4>
                    <div className="text-xs text-text-secondary space-y-1">
                      <div>Email: contractor@openbooq.com</div>
                      <div>Password: contractor123</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Support */}
              <div className="text-center pt-4 border-t border-border">
                <p className="text-sm text-text-secondary mb-3">Need help accessing your account?</p>
                <Button variant="outline" size="sm">
                  Contact Support
                </Button>
              </div>
            </div>
          </div>

          {/* Portal Features & Updates */}
          <div className="space-y-8">
            {/* Features */}
            <div className="space-y-6">
              <h3 className="text-xl font-jakarta font-bold text-text-primary">
                Portal Features
              </h3>
              
              <div className="grid gap-4">
                {portalFeatures?.map((feature) => (
                  <div 
                    key={feature?.id}
                    className="flex items-start space-x-4 p-4 bg-white border border-border rounded-lg hover:shadow-trust transition-all duration-300"
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={feature?.icon} size={16} className="text-white" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-jakarta font-semibold text-text-primary">{feature?.title}</h4>
                      <p className="text-sm text-text-secondary">{feature?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Updates */}
            <div className="space-y-6">
              <h3 className="text-xl font-jakarta font-bold text-text-primary">
                Recent Updates
              </h3>
              
              <div className="space-y-4">
                {recentUpdates?.map((update) => (
                  <div 
                    key={update?.id}
                    className="p-4 bg-surface rounded-lg border-l-4 border-primary"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-jakarta font-semibold text-text-primary">{update?.title}</h4>
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                        {update?.type}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary mb-2">{update?.description}</p>
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={12} className="text-text-secondary" />
                      <span className="text-xs text-text-secondary">{update?.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6">
              <h4 className="font-jakarta font-semibold text-text-primary mb-4">Platform Statistics</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-jakarta font-bold text-primary">500+</div>
                  <div className="text-sm text-text-secondary">Active Contractors</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-jakarta font-bold text-primary">2,500+</div>
                  <div className="text-sm text-text-secondary">Jobs Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-jakarta font-bold text-primary">98%</div>
                  <div className="text-sm text-text-secondary">Satisfaction Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-jakarta font-bold text-primary">$15M+</div>
                  <div className="text-sm text-text-secondary">Contractor Earnings</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortalAccess;